import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { BooleanModelOptions } from '../options/boolean-model-options';
import { DateModelOptions } from '../options/date-model-options';
import { DropdownModelOptions } from '../options/dropdown-model-options';
import { RadioModelOptions } from '../options/radio-model-options';
import { TextModelOptions } from '../options/text-model-options';
import { BooleanModel } from '../primitive/boolean.model';
import { DateModel } from '../primitive/date.model';
import { TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import { DropdownModel } from './dropdown.model';
import { RadioModel } from './radio.model';
import moment from 'moment';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { NumberModelOptions } from '../options/number-model-options';
import { NumberModel } from '../primitive/number.model';

export const customerLabel = 'Customer';
export const healthcareInternalLabel = 'Healthcare Internal';

export class ConfirmationComplaintsQcaFieldsModel extends CompositeModel {
    public acc: RadioModel<TextModel>;
    public accidentalRadiationOccurrences: RadioModel<TextModel>;
    public age: NumberModel;
    public allegedMedical: RadioModel<TextModel>;
    public damage: RadioModel<TextModel>;
    public dateFirstEvent: DateModel;
    public dateNotified: DateModel;
    public initiatedBy: RadioModel<TextModel>;
    public partsReplaced: RadioModel<TextModel>;
    public identity: DropdownModel<TextModel>;
    public injury: RadioModel<TextModel>;
    public medical: RadioModel<TextModel>;
    public gender: RadioModel<TextModel>;
    public weight: TextModel;
    public deviceUsed: RadioModel<TextModel>;
    public diffInstance: RadioModel<TextModel>;
    public diffSample: RadioModel<TextModel>;
    public frequency: TextModel;
    public maintenance: RadioModel<TextModel>;
    public malfunction: RadioModel<TextModel>;
    public otherTests: RadioModel<TextModel>;
    public primaryEvent: DropdownModel<TextModel>;
    public questioned: RadioModel<TextModel>;
    public repeatTesting: RadioModel<TextModel>;
    public reported: RadioModel<TextModel>;
    public reportedMedical: RadioModel<TextModel>;
    public safetyPerson: TextModel;
    public safetyUnits: TextModel;
    public actualFailure: RadioModel<TextModel>;
    public correctedReport: RadioModel<TextModel>;
    public feedback: RadioModel<TextModel>;

    // UI-only models
    public ageSexWeightAvailable: DisplayDecorator<BooleanModel>;

    public constructor() {
        super('QCAFields', {});

        const yesOption = new DisplayDecorator(
            ConfigConstants.optionYes,
            new TextModel(ConfigConstants.optionYes, ConfigConstants.optionYes),
        );
        const noOption = new DisplayDecorator(
            ConfigConstants.optionNo,
            new TextModel(ConfigConstants.optionNo, ConfigConstants.optionNo),
        );
        const naOption = new DisplayDecorator(
            ConfigConstants.optionNA,
            new TextModel(ConfigConstants.optionNA, ConfigConstants.optionNA),
        );
        const unknownOption = new DisplayDecorator(
            ConfigConstants.optionUnknown,
            new TextModel(ConfigConstants.optionUnknown, ConfigConstants.optionUnknown),
        );

        const yesNoOptionGroup = [yesOption, noOption];
        const yesNoUnknownOptionGroup = [yesOption, noOption, unknownOption];
        const yesNoUnknownNaOptionGroup = [yesOption, noOption, unknownOption, naOption];

        this.acc = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'Acc',
                    yesNoUnknownOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.accidentalRadiationOccurrences = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'RadiationDamage',
                    yesNoOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.age = <NumberModel>this.set(
            new NumberModel(
                'Age',
                undefined,
                new NumberModelOptions({
                    required: true,
                    maxValue: 150,
                    pattern: ConfigConstants.validationBackupPattern.Integer,
                    patternScan: ConfigConstants.validationBackupPattern.Integer,
                }),
            ),
        );
        this.allegedMedical = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'AllegedMedical',
                    yesNoUnknownOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.damage = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'Damage',
                    yesNoUnknownOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.dateFirstEvent = <DateModel>(
            this.set(
                new DateModel(
                    'DateFirstEvent',
                    undefined,
                    new DateModelOptions({ required: true, maxValue: moment().endOf('day') }),
                ),
            )
        );

        this.dateNotified = <DateModel>(
            this.set(
                new DateModel(
                    'DateNotified',
                    undefined,
                    new DateModelOptions({ required: true, maxValue: moment().endOf('day') }),
                ),
            )
        );

        const initiatedByOptionGroup = [
            new DisplayDecorator<TextModel>('Customer', new TextModel(customerLabel, customerLabel)),
            new DisplayDecorator<TextModel>(
                'Healthineers internal',
                new TextModel(healthcareInternalLabel, healthcareInternalLabel),
            ),
        ];
        this.initiatedBy = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'InitiatedBy',
                    initiatedByOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.partsReplaced = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'PartsReplaced',
                    yesNoUnknownOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        const identityOptionGroup = [
            new DisplayDecorator('Patient', new TextModel('patient', 'Patient')),
            new DisplayDecorator('Operator', new TextModel('operator', 'Operator')),
            new DisplayDecorator('Siemens employee', new TextModel('siemensEmployee', 'Siemens employee')),
            new DisplayDecorator('Other', new TextModel('other', 'Other')),
            new DisplayDecorator('Not applicable', new TextModel('n/a', 'N/A')),
        ];
        this.identity = <DropdownModel<TextModel>>(
            this.set(
                new DropdownModel<TextModel>(
                    'Identity',
                    identityOptionGroup,
                    new DropdownModelOptions({ required: true }),
                ),
            )
        );
        this.injury = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'Injury',
                    yesNoUnknownOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.medical = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'Medical',
                    yesNoUnknownOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        const genderOptionsGroup = [
            new DisplayDecorator('female', new TextModel('female', 'F')),
            new DisplayDecorator('male', new TextModel('male', 'M')),
            new DisplayDecorator('unknown', new TextModel('unknown', 'U')),
        ];
        this.gender = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'Gender',
                    genderOptionsGroup,
                    new RadioModelOptions({ required: true, initialSelection: -1 }),
                ),
            )
        );
        this.weight = <TextModel>this.set(
            new TextModel(
                'Weight',
                undefined,
                new TextModelOptions({
                    maxLength: 3,
                    required: true,
                    pattern: ConfigConstants.realNumberRegex,
                    inputType: ConfigConstants.inputTypeNumeric,
                }),
            ),
        );
        this.deviceUsed = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'DeviceUsed',
                    yesNoUnknownOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.diffInstance = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'DiffInstance',
                    yesNoOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.diffSample = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'DiffSample',
                    yesNoOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.frequency = <TextModel>(
            this.set(new TextModel('Frequency', '', new TextModelOptions({ maxLength: 30, required: true })))
        );
        this.maintenance = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'Maintenance',
                    yesNoUnknownOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.malfunction = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'Malfunction',
                    yesNoUnknownOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.otherTests = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'OtherTests',
                    yesNoOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        const primaryEventOptionsGroup = [
            new DisplayDecorator(
                'MALFUNCTION - INS, CONS, SFTW',
                new TextModel('malfunction', 'MALFUNCTION - INS, CONS, SFTW'),
            ),
            new DisplayDecorator(
                'POTEN. HZD: SPLASH,SMOKE,FIRE',
                new TextModel('hazard', 'POTEN. HZD: SPLASH,SMOKE,FIRE'),
            ),
            new DisplayDecorator('Death', new TextModel('death', 'Death')),
            new DisplayDecorator(
                'Injury/Illness (or potential)',
                new TextModel('injury', 'Injury/Illness (or potential)'),
            ),
            new DisplayDecorator(
                'Potential Hazard (Leak, Fire)',
                new TextModel('hazard2', 'Potential Hazard (Leak, Fire)'),
            ),
            new DisplayDecorator('ARO (Accidental Radiation)', new TextModel('aro', 'ARO (Accidental Radiation)')),
            new DisplayDecorator('Unflagged error', new TextModel('unflaggedError', 'Unflagged error')),
            new DisplayDecorator(
                'Malfunction (HW, SW, Assay)',
                new TextModel('malfunction2', 'Malfunction (HW, SW, Assay)'),
            ),
            new DisplayDecorator('Flagged error', new TextModel('flaggedError', 'Flagged error')),
            new DisplayDecorator('Non-standard use', new TextModel('nonStdUse', 'Non-standard use')),
            new DisplayDecorator('Discordant Result(s)', new TextModel('discordantRes', 'Discordant Result(s)')),
            new DisplayDecorator('Other', new TextModel('other', 'Other')),
        ];
        this.primaryEvent = <DropdownModel<TextModel>>(
            this.set(
                new DropdownModel<TextModel>(
                    'PrimaryEvent',
                    primaryEventOptionsGroup,
                    new DropdownModelOptions({ required: true }),
                ),
            )
        );
        this.questioned = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'Questioned',
                    yesNoUnknownOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.repeatTesting = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'RepeatTesting',
                    yesNoOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.reported = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'Reported',
                    yesNoUnknownOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.reportedMedical = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'ReportedMedical',
                    yesNoUnknownNaOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.safetyPerson = <TextModel>(
            this.set(new TextModel('SafetyPerson', '', new TextModelOptions({ maxLength: 30, required: true })))
        );
        this.safetyUnits = <TextModel>(
            this.set(new TextModel('SafetyUnits', '', new TextModelOptions({ maxLength: 30, required: true })))
        );
        this.actualFailure = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'ActualFailure',
                    yesNoUnknownNaOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        this.correctedReport = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'CorrectedReport',
                    yesNoOptionGroup,
                    new RadioModelOptions({ initialSelection: -1, required: true }),
                ),
            )
        );
        // the value for this option is indeed an empty string instead of the usual 'No' m(
        const emptyNoOption = new DisplayDecorator(
            ConfigConstants.optionNo,
            new TextModel(ConfigConstants.optionNo, ''),
        );

        const yesEmptyNoOptionGroup = [yesOption, emptyNoOption];
        this.feedback = <RadioModel<TextModel>>(
            this.set(
                new RadioModel(
                    'Feedback',
                    yesEmptyNoOptionGroup,
                    new RadioModelOptions({ required: true, initialSelection: 0 }),
                ),
            )
        );

        // UI-only models
        this.ageSexWeightAvailable = new DisplayDecorator<BooleanModel>(
            'Age, sex, and weight available?',
            new BooleanModel('aswAvailable', false, new BooleanModelOptions({ showLabel: false })),
        );
    }

    public getOutModel(): ODataObject {
        const outModel = super.getOutModel();

        // SAP expects a string value, but UI logic needs a number. Therefore, converting here.
        outModel.Age = outModel.Age?.toString() ?? '';

        return outModel;
    }

    public toJSON(): JsonConfirmationComplaintsQcaFieldsModel {
        return {
            acc: this.acc.getSelectedIndex(),
            accidentalRadiationOccurrences: this.accidentalRadiationOccurrences.getSelectedIndex(),
            actualFailure: this.actualFailure.getSelectedIndex(),
            age: this.age.value,
            ageSexWeightAvailable: this.ageSexWeightAvailable.value,
            allegedMedical: this.allegedMedical.getSelectedIndex(),
            correctedReport: this.correctedReport.getSelectedIndex(),
            damage: this.damage.getSelectedIndex(),
            dateFirstEvent: this.dateFirstEvent.value.toISOString(),
            dateNotified: this.dateNotified.value.toISOString(),
            deviceUsed: this.deviceUsed.getSelectedIndex(),
            diffInstance: this.diffInstance.getSelectedIndex(),
            diffSample: this.diffSample.getSelectedIndex(),
            frequency: this.frequency.value,
            gender: this.gender.getSelectedIndex(),
            identity: this.identity.selectedEntry?.value,
            initiatedBy: this.initiatedBy.getSelectedIndex(),
            injury: this.injury.getSelectedIndex(),
            maintenance: this.maintenance.getSelectedIndex(),
            malfunction: this.malfunction.getSelectedIndex(),
            medical: this.medical.getSelectedIndex(),
            otherTests: this.otherTests.getSelectedIndex(),
            partsReplaced: this.partsReplaced.getSelectedIndex(),
            primaryEvent: this.primaryEvent.selectedEntry?.value,
            questioned: this.questioned.getSelectedIndex(),
            repeatTesting: this.repeatTesting.getSelectedIndex(),
            reported: this.reported.getSelectedIndex(),
            reportedMedical: this.reportedMedical.getSelectedIndex(),
            safetyPerson: this.safetyPerson.value,
            safetyUnits: this.safetyUnits.value,
            weight: this.weight.value,
            feedback: this.feedback.getSelectedIndex(),
        };
    }
}

export class ConfirmationComplaintsQcaFieldsModelFromJson extends ConfirmationComplaintsQcaFieldsModel {
    public constructor(data: JsonConfirmationComplaintsQcaFieldsModel) {
        super();
        this.acc.selectByIndex(data.acc);
        this.accidentalRadiationOccurrences.selectByIndex(data.accidentalRadiationOccurrences);
        this.age.value = data.age;
        this.allegedMedical.selectByIndex(data.allegedMedical);
        this.damage.selectByIndex(data.damage);
        this.dateFirstEvent.value = moment(data.dateFirstEvent);
        this.dateNotified.value = moment(data.dateNotified);
        this.initiatedBy.selectByIndex(data.initiatedBy);
        this.partsReplaced.selectByIndex(data.partsReplaced);
        this.identity.selectByValue(data.identity);
        this.injury.selectByIndex(data.injury);
        this.medical.selectByIndex(data.medical);
        this.gender.selectByIndex(data.gender);
        this.weight.value = data.weight;
        this.deviceUsed.selectByIndex(data.deviceUsed);
        this.diffInstance.selectByIndex(data.diffInstance);
        this.diffSample.selectByIndex(data.diffSample);
        this.frequency.value = data.frequency;
        this.maintenance.selectByIndex(data.maintenance);
        this.malfunction.selectByIndex(data.malfunction);
        this.otherTests.selectByIndex(data.otherTests);
        this.primaryEvent.selectByValue(data.primaryEvent);
        this.questioned.selectByIndex(data.questioned);
        this.repeatTesting.selectByIndex(data.repeatTesting);
        this.reported.selectByIndex(data.reported);
        this.reportedMedical.selectByIndex(data.reportedMedical);
        this.safetyPerson.value = data.safetyPerson;
        this.safetyUnits.value = data.safetyUnits;
        this.actualFailure.selectByIndex(data.actualFailure);
        this.correctedReport.selectByIndex(data.correctedReport);
        this.ageSexWeightAvailable.value = data.ageSexWeightAvailable;
        this.feedback.selectByIndex(data.feedback);
    }
}

export interface JsonConfirmationComplaintsQcaFieldsModel {
    acc: number;
    accidentalRadiationOccurrences: number;
    age: number | undefined;
    allegedMedical: number;
    damage: number;
    dateFirstEvent: string;
    dateNotified: string;
    initiatedBy: number;
    partsReplaced: number;
    identity: string;
    injury: number;
    medical: number;
    gender: number;
    weight: string;
    deviceUsed: number;
    diffInstance: number;
    diffSample: number;
    frequency: string;
    maintenance: number;
    malfunction: number;
    otherTests: number;
    primaryEvent: string;
    questioned: number;
    repeatTesting: number;
    reported: number;
    reportedMedical: number;
    safetyPerson: string;
    safetyUnits: string;
    actualFailure: number;
    correctedReport: number;
    ageSexWeightAvailable: boolean;
    feedback: number;
}
